import React, { useEffect, useMemo, useRef, useState } from "react";

import { ZonedDateTime } from "@js-joda/core";
import { useGetObjectDataset } from "api/api-hooks/object-dataset-hooks";
import { ObjectDatasetDataDto } from "api/model/object-dataset/types";
import DataTable from "components/basic/data-table/DataTable";
import DateTimeRange from "components/basic/date-time-range/DateTimeRange";
import { TableColumn } from "components/pages/measurement-devices/utils/types";
import { isSameDay, parseISO } from "date-fns";
import useCurrentCompanyContext from "hooks/useCurrentCompanyContext";
import { useDisplayTimezone } from "hooks/useTimezone";
import { SortOrder } from "primereact/api";
import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { ProgressSpinner } from "primereact/progressspinner";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { T } from "services/lang/LanguageService";
import { useUserState } from "store/user-store";
import { DATE_PATTERN_MINUTES } from "utils/constants";
import { addToDate, fmtDatetime, toChronoUnit, toNativeDate } from "utils/time-utils";
import { getLocale } from "utils/utils";
import { DatasetLoadingConf } from "../charts/interfaces";
import "./ObjectDataset.scss";
import { ObjectDatasetConfig } from "./ObjectDatasetConfig";

const fileUrl = (
  companyReference: string | undefined,
  objectDataIdentifier: string | undefined,
  timestamp: string | undefined,
  content: string
) => {
  if (!companyReference || !objectDataIdentifier || !timestamp) return "";
  return encodeURI(
    `${window.contextRoot}/rest/object-dataset/${companyReference}/${objectDataIdentifier}/${timestamp}/${content}`
  );
};

export default function ObjectDataset(props: ObjectDatasetConfig) {
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [objectData, setObjectData] = useState<ObjectDatasetDataDto | null>(null);
  const [content, setContent] = useState("");
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);

  const userLang = useUserState.getState().currentUser?.preferences.language;

  const iframeRef = useRef<HTMLIFrameElement>(null);

  const company = useCurrentCompanyContext();

  const { data } = useGetObjectDataset(props.identifier ?? "", company?.companyReference ?? "", {
    enabled: !!company?.companyReference || !!props.identifier,
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if (data && data.data?.length > 0) {
      const sortData = data.data.sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      const latest = sortData[0];
      setObjectData(latest);
      setSelectedDate(parseISO(latest.timestamp));
    }
  }, [data]);

  useEffect(() => {
    if (objectData) {
      const foundFile = objectData.fileNames.find(file => props.initialFileName && file === props.initialFileName);
      setContent(foundFile ?? objectData.fileNames[0]);
    }
  }, [objectData, props.initialFileName]);

  const handleIframeLoad = () => {
    const iframe = iframeRef.current;
    if (iframe && iframe.contentWindow) {
      const iframeDoc = iframe.contentWindow.document;
      const height = Array.from(iframeDoc.body.children).reduce((acc, child) => {
        return acc + child.scrollHeight;
      }, 0);
      iframe.style.height = `${height + 40}px`;
    }
    setIsLoadingFile(false);
  };

  const availableTimestamps = useMemo(() => {
    return (data?.data ?? []).map(d => ({
      date: parseISO(d.timestamp),
      item: d,
    }));
  }, [data]);

  const availableDates = useMemo(() => {
    return availableTimestamps.map(d => d.date);
  }, [availableTimestamps]);

  const timesForSelectedDate = useMemo(() => {
    if (!selectedDate) return [];
    return availableDates.filter(d => isSameDay(d, selectedDate));
  }, [availableDates, selectedDate]);

  const handleOnDateChange = (date: Date | null) => {
    setSelectedDate(date);
    const matched = availableTimestamps.find(d => d.date.getTime() === date?.getTime());
    if (matched) {
      setObjectData(matched.item);
      setContent(matched.item.fileNames[0]);
    }
  };

  return (
    <>
      {!content && <div className="text-sm text-gray-500 align-self-center my-5">{T("message.no-models")}</div>}
      {content && data?.type !== "GENERAL" && (
        <div className="flex flex-wrap gap-3">
          <div className="flex gap-2 w-full flex-wrap">
            <DatePicker
              selected={selectedDate}
              onChange={handleOnDateChange}
              showTimeSelect
              includeDates={availableDates}
              includeTimes={timesForSelectedDate}
              dateFormat={DATE_PATTERN_MINUTES}
              placeholderText={T("select.date-and-time")}
              locale={getLocale(userLang)}
              timeIntervals={1}
            />
            {objectData && objectData.fileNames.length > 1 && data?.type !== "HTML" && (
              <Dropdown options={objectData.fileNames} value={content} onChange={e => setContent(e.value)} />
            )}
            <Button
              type="button"
              onClick={() => window.open(`${fileUrl}?dl=true`, "_blank")}
              icon="pi pi-download"
              label={T("action.download")}
              disabled={!objectData}
              className="component-editor-button icon-green"
            />
          </div>
          {isLoadingFile && (
            <div className="w-full align-self-center">
              <ProgressSpinner style={{ width: "10rem" }} />
            </div>
          )}

          {content && data?.type === "IMAGE" && (
            <img
              src={fileUrl(company?.companyReference, props.identifier, objectData?.timestamp, content)}
              alt="model-view"
              loading="lazy"
              onLoad={() => setIsLoadingFile(false)}
              style={{ objectFit: "contain", width: "100%", height: "auto" }}
            />
          )}
          {content && data?.type !== "IMAGE" && (
            <iframe
              ref={iframeRef}
              loading="lazy"
              src={fileUrl(company?.companyReference, props.identifier, objectData?.timestamp, content)}
              title="model-view"
              onLoad={handleIframeLoad}
              style={{ maxHeight: "200vh" }}
              className="flex-1 border-none"
            />
          )}
        </div>
      )}
      {data?.type === "GENERAL" && (
        <ObjectDatasetGeneral
          data={data.data}
          objectIdentifier={props.identifier}
          loadConf={props.loadConf}
          rows={props.rows}
        />
      )}
    </>
  );
}

type ObjectDatasetGeneralProps = {
  data: ObjectDatasetDataDto[];
  objectIdentifier: string;
  loadConf: DatasetLoadingConf | undefined;
  rows?: number | null;
};

const ObjectDatasetGeneral: React.FC<ObjectDatasetGeneralProps> = ({ data, objectIdentifier, loadConf, rows }) => {
  const displayTz = useDisplayTimezone();

  const initFrom = useMemo(() => {
    const lag = loadConf?.lag ?? 1;
    const lagUnit = loadConf?.lagUnit ?? "MONTHS";
    const alignUnit = loadConf?.alignUnit ?? "MINUTES";
    return addToDate(ZonedDateTime.now(displayTz).truncatedTo(toChronoUnit(alignUnit)), -lag, lagUnit);
  }, [displayTz, loadConf?.alignUnit, loadConf?.lag, loadConf?.lagUnit]);

  const initTo = useMemo(() => {
    const lead = loadConf?.lead ?? 1;
    const leadUnit = loadConf?.leadUnit ?? "MONTHS";
    const alignUnit = loadConf?.alignUnit ?? "MINUTES";
    return addToDate(ZonedDateTime.now(displayTz).truncatedTo(toChronoUnit(alignUnit)), lead, leadUnit);
  }, [displayTz, loadConf?.alignUnit, loadConf?.lead, loadConf?.leadUnit]);

  const [from, setFrom] = useState(initFrom);
  const [to, setTo] = useState(initTo);

  useEffect(() => {
    setFrom(initFrom);
  }, [initFrom]);

  useEffect(() => {
    setTo(initTo);
  }, [initTo]);

  const company = useCurrentCompanyContext();

  const flatFiles = data.flatMap(d =>
    d.fileNames.map(fileName => ({
      ...d,
      fileNames: [fileName],
    }))
  );

  const filteredFlatFiles = useMemo(() => {
    if (!from || !to) return flatFiles;
    const fromTime = from.getTime();
    const toTime = to.getTime();

    return flatFiles.filter(f => {
      if (!f.timestamp) return false;
      const t = toNativeDate(f.timestamp).getTime();
      return t >= fromTime && t <= toTime;
    });
  }, [flatFiles, from, to]);

  const downloadButton = (rowData: ObjectDatasetDataDto) => (
    <Button
      type="button"
      onClick={() => {
        window.open(`${fileUrl(company?.companyReference, objectIdentifier, rowData.timestamp, rowData.fileNames[0])}?dl=true`);
      }}
      icon="pi pi-download"
      className="component-editor-button icon-green"
    />
  );

  const columns: TableColumn<ObjectDatasetDataDto>[] = [
    {
      header: T("word.date"),
      field: "timestamp",
      columnKey: "timestamp",
      body: rowData => rowData.timestamp && fmtDatetime(rowData.timestamp, displayTz),
      sortable: true,
    },
    {
      header: T("word.file"),
      field: "fileNames",
      columnKey: "fileNames",
      body: rowData => rowData.fileNames.join(", "),
      sortable: true,
    },
    {
      header: T("word.updated-at"),
      field: "updatedAt",
      columnKey: "updatedAt",
      body: rowData => rowData.updatedAt && fmtDatetime(rowData.updatedAt, displayTz),
      sortable: true,
    },
    {
      header: T("action.download"),
      columnKey: "download",
      body: downloadButton,
    },
  ];

  const rowsPerPageOptions = useMemo(() => [...new Set([5, 10, 25, 50, rows ?? 10])].sort((a, b) => a - b), [rows]);

  return (
    <>
      <div className="flex gap-2 mb-3">
        <DateTimeRange dateFrom={from} dateTo={to} selectDateFrom={setFrom} selectDateTo={setTo} />
      </div>
      <DataTable
        columns={columns}
        data={filteredFlatFiles}
        tableConfig={{ rows: rows ?? 10, sortField: "timestamp", sortOrder: SortOrder.DESC, rowsPerPageOptions }}
      />
    </>
  );
};
